import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "@/utils/languageRouter";
import axios from "axios";
import { 
  Calendar, 
  MapPin, 
  Clock, 
  Users, 
  Car, 
  Mountain, 
  Sparkles,
  Eye,
  CalendarCheck,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DeparturesPage = () => {
  const { t, i18n } = useTranslation();
  const { lang: urlLang } = useParams();
  const lang = urlLang || i18n.language;
  const currentLang = SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  
  const localizedLink = (path) => {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `/${currentLang}${cleanPath}`;
  };
  
  const [departures, setDepartures] = useState({
    circuits_4x4: [],
    treks: [],
    stages: []
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("circuits");

  useEffect(() => {
    fetchDepartures();
  }, []);

  const fetchDepartures = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/departures/all`);
      setDepartures(res.data);
    } catch (err) {
      console.error("Error fetching departures:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const getSpotsBadge = (remaining, total) => {
    if (remaining <= 0) {
      return <Badge variant="destructive" className="whitespace-nowrap">Complet</Badge>;
    }
    if (remaining <= 3) {
      return <Badge className="bg-orange-500 whitespace-nowrap">{remaining} {lang === "fr" ? "places" : "spots"}</Badge>;
    }
    return <Badge variant="secondary" className="whitespace-nowrap">{remaining}/{total} {lang === "fr" ? "places" : "spots"}</Badge>;
  };

  const getDifficultyBadge = (difficulty) => {
    const colors = {
      easy: "bg-green-100 text-green-800",
      moderate: "bg-yellow-100 text-yellow-800",
      challenging: "bg-red-100 text-red-800"
    };
    const labels = {
      easy: { fr: "Facile", en: "Easy" },
      moderate: { fr: "Modéré", en: "Moderate" },
      challenging: { fr: "Difficile", en: "Challenging" }
    };
    return (
      <Badge className={colors[difficulty] || "bg-gray-100"}>
        {labels[difficulty]?.[lang] || difficulty}
      </Badge>
    );
  };

  const CircuitTable = ({ items, type }) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="font-semibold">{lang === "fr" ? "Circuit" : "Tour"}</TableHead>
            <TableHead className="font-semibold">{lang === "fr" ? "Départ" : "Departure"}</TableHead>
            <TableHead className="font-semibold">{lang === "fr" ? "Dates" : "Dates"}</TableHead>
            <TableHead className="font-semibold">{lang === "fr" ? "Durée" : "Duration"}</TableHead>
            <TableHead className="font-semibold">{lang === "fr" ? "Prix" : "Price"}</TableHead>
            <TableHead className="font-semibold">{lang === "fr" ? "Places" : "Spots"}</TableHead>
            <TableHead className="font-semibold text-right">{lang === "fr" ? "Actions" : "Actions"}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                {lang === "fr" ? "Aucun départ programmé" : "No departures scheduled"}
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <TableRow key={item.id} className="hover:bg-muted/30">
                <TableCell>
                  <div className="flex items-center gap-3">
                    {item.image_url && (
                      <img 
                        src={item.image_url} 
                        alt="" 
                        className="w-12 h-12 rounded-lg object-cover hidden sm:block"
                      />
                    )}
                    <div>
                      <p className="font-medium line-clamp-1">
                        {item.title?.[lang] || item.title?.fr}
                      </p>
                      {item.difficulty && (
                        <div className="mt-1">
                          {getDifficultyBadge(item.difficulty)}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 text-sm">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{item.departure_city}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div className="font-medium">{formatDate(item.start_date)}</div>
                    <div className="text-muted-foreground text-xs">→ {formatDate(item.end_date)}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 text-sm">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{item.duration_days} {lang === "fr" ? "jours" : "days"}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="font-semibold text-primary">€{item.price}</span>
                </TableCell>
                <TableCell>
                  {getSpotsBadge(item.remaining_spots, item.available_spots)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Link to={localizedLink(`/circuit/${item.circuit_id}`)}>
                      <Button variant="outline" size="sm" className="gap-1.5">
                        <Eye className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">{lang === "fr" ? "Détails" : "Details"}</span>
                      </Button>
                    </Link>
                    {item.remaining_spots > 0 && (
                      <Link to={localizedLink(`/circuit/${item.circuit_id}`)}>
                        <Button size="sm" className="gap-1.5">
                          <CalendarCheck className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">{lang === "fr" ? "Réserver" : "Book"}</span>
                        </Button>
                      </Link>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  const StagesTable = ({ items }) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="font-semibold">{lang === "fr" ? "Stage" : "Retreat"}</TableHead>
            <TableHead className="font-semibold">{lang === "fr" ? "Lieu" : "Location"}</TableHead>
            <TableHead className="font-semibold">{lang === "fr" ? "Dates" : "Dates"}</TableHead>
            <TableHead className="font-semibold">{lang === "fr" ? "Durée" : "Duration"}</TableHead>
            <TableHead className="font-semibold">{lang === "fr" ? "Prix" : "Price"}</TableHead>
            <TableHead className="font-semibold">{lang === "fr" ? "Places" : "Spots"}</TableHead>
            <TableHead className="font-semibold text-right">{lang === "fr" ? "Actions" : "Actions"}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                {lang === "fr" ? "Aucun stage programmé" : "No retreats scheduled"}
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <TableRow key={item.id} className="hover:bg-muted/30">
                <TableCell>
                  <div className="flex items-center gap-3">
                    {item.image_url && (
                      <img 
                        src={item.image_url} 
                        alt="" 
                        className="w-12 h-12 rounded-lg object-cover hidden sm:block"
                      />
                    )}
                    <div>
                      <p className="font-medium line-clamp-1">
                        {item.title?.[lang] || item.title?.fr}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {lang === "fr" ? "par" : "by"} {item.collaborator_name}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 text-sm">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{item.location}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div className="font-medium">{formatDate(item.start_date)}</div>
                    <div className="text-muted-foreground text-xs">→ {formatDate(item.end_date)}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 text-sm">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{item.duration_days} {lang === "fr" ? "jours" : "days"}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="font-semibold text-primary">€{item.price}</span>
                </TableCell>
                <TableCell>
                  {getSpotsBadge(item.remaining_spots, item.available_spots)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Link to={localizedLink(`/stages/${item.slug}`)}>
                      <Button variant="outline" size="sm" className="gap-1.5">
                        <Eye className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">{lang === "fr" ? "Détails" : "Details"}</span>
                      </Button>
                    </Link>
                    {item.remaining_spots > 0 && (
                      <Link to={localizedLink(`/stages/${item.slug}`)}>
                        <Button size="sm" className="gap-1.5">
                          <CalendarCheck className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">{lang === "fr" ? "Réserver" : "Book"}</span>
                        </Button>
                      </Link>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="departures-page">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary/10 via-background to-accent/10 py-16 mb-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Calendar className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-4">
            {lang === "fr" ? "Prochains Départs" : "Upcoming Departures"}
          </h1>
          <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            {lang === "fr" 
              ? "Votre prochaine aventure saharienne n'attend que vous ! Découvrez nos dates de départ confirmées pour les circuits 4x4, treks et stages. Places limitées pour garantir une expérience authentique et personnalisée. Réservez dès maintenant pour vivre des moments inoubliables sous les étoiles du désert."
              : "Your next Saharan adventure is waiting for you! Discover our confirmed departure dates for 4x4 tours, treks and retreats. Limited spots to ensure an authentic and personalized experience. Book now to experience unforgettable moments under the desert stars."
            }
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8 h-auto p-1">
              <TabsTrigger 
                value="circuits" 
                className="flex items-center gap-2 py-3 data-[state=active]:bg-primary data-[state=active]:text-white"
                data-testid="tab-circuits"
              >
                <Car className="h-4 w-4" />
                <span className="hidden sm:inline">Circuits 4x4</span>
                <span className="sm:hidden">4x4</span>
                {departures.circuits_4x4.length > 0 && (
                  <Badge variant="secondary" className="ml-1 bg-white/20">
                    {departures.circuits_4x4.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="treks" 
                className="flex items-center gap-2 py-3 data-[state=active]:bg-primary data-[state=active]:text-white"
                data-testid="tab-treks"
              >
                <Mountain className="h-4 w-4" />
                <span className="hidden sm:inline">Trekking</span>
                <span className="sm:hidden">Trek</span>
                {departures.treks.length > 0 && (
                  <Badge variant="secondary" className="ml-1 bg-white/20">
                    {departures.treks.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="stages" 
                className="flex items-center gap-2 py-3 data-[state=active]:bg-primary data-[state=active]:text-white"
                data-testid="tab-stages"
              >
                <Sparkles className="h-4 w-4" />
                <span className="hidden sm:inline">Stages & Retraites</span>
                <span className="sm:hidden">Stages</span>
                {departures.stages.length > 0 && (
                  <Badge variant="secondary" className="ml-1 bg-white/20">
                    {departures.stages.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="circuits" className="mt-0">
              <div className="bg-card rounded-2xl border border-border overflow-hidden">
                <div className="p-4 md:p-6 border-b border-border bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-accent/20 rounded-lg">
                      <Car className="h-5 w-5 text-accent" />
                    </div>
                    <div>
                      <h2 className="font-serif text-xl font-semibold">
                        {lang === "fr" ? "Circuits 4x4" : "4x4 Tours"}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {lang === "fr" 
                          ? "Explorez le désert en véhicule tout-terrain"
                          : "Explore the desert by off-road vehicle"
                        }
                      </p>
                    </div>
                  </div>
                </div>
                <CircuitTable items={departures.circuits_4x4} type="4x4" />
              </div>
            </TabsContent>

            <TabsContent value="treks" className="mt-0">
              <div className="bg-card rounded-2xl border border-border overflow-hidden">
                <div className="p-4 md:p-6 border-b border-border bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/20 rounded-lg">
                      <Mountain className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-serif text-xl font-semibold">
                        {lang === "fr" ? "Trekking" : "Trekking"}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {lang === "fr" 
                          ? "Randonnées pédestres à travers les dunes"
                          : "Hiking adventures through the dunes"
                        }
                      </p>
                    </div>
                  </div>
                </div>
                <CircuitTable items={departures.treks} type="trekking" />
              </div>
            </TabsContent>

            <TabsContent value="stages" className="mt-0">
              <div className="bg-card rounded-2xl border border-border overflow-hidden">
                <div className="p-4 md:p-6 border-b border-border bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <Sparkles className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <h2 className="font-serif text-xl font-semibold">
                        {lang === "fr" ? "Stages & Retraites" : "Retreats & Workshops"}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {lang === "fr" 
                          ? "Yoga, méditation, art et bien-être au cœur du désert"
                          : "Yoga, meditation, art and wellness in the heart of the desert"
                        }
                      </p>
                    </div>
                  </div>
                </div>
                <StagesTable items={departures.stages} />
              </div>
            </TabsContent>
          </Tabs>
        )}

        {/* Call to Action */}
        <div className="mt-12 bg-gradient-to-r from-primary/10 to-accent/10 rounded-3xl p-8 md:p-12 text-center">
          <h3 className="font-serif text-2xl md:text-3xl font-semibold mb-4">
            {lang === "fr" 
              ? "Vous ne trouvez pas la date idéale ?"
              : "Can't find the perfect date?"
            }
          </h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            {lang === "fr"
              ? "Contactez-nous pour organiser un départ sur mesure ou rejoindre notre liste d'attente pour les prochaines dates."
              : "Contact us to arrange a custom departure or join our waiting list for upcoming dates."
            }
          </p>
          <Link to={localizedLink("/contact")}>
            <Button size="lg" className="rounded-full">
              {lang === "fr" ? "Nous contacter" : "Contact us"}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DeparturesPage;
